import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import ImageUpload from "./ImageUpload";
import webApiClient from "../../api/client";
import "./InstructorCoverUpload.css";

/**
 * InstructorCoverUpload — rectangular cover image editor for the instructor public booking card.
 * Wraps the shared <ImageUpload> and adds Save/Cancel. Uploads to the separate
 * instructorProfile.coverImageKey field — independent of the school org cover.
 */
const InstructorCoverUpload = ({ currentImage, onSaveSuccess, onSaveError, onPreviewChange, onDeleteSuccess, externalPendingBase64, onExternalCancel }) => {
    const [coverImageFile, setCoverImageFile] = useState(null);
    const [coverImageChanged, setCoverImageChanged] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // A newly chosen preset supersedes a pending file (the mirror of onImageSelect
    // cancelling the preset), so the displayed image and the Save payload always agree.
    useEffect(() => {
        if (externalPendingBase64) {
            setCoverImageFile(null);
            setCoverImageChanged(false);
        }
    }, [externalPendingBase64]);

    const hasPendingChanges = coverImageChanged || !!externalPendingBase64;
    const displayImage = coverImageFile?.thumbnail || externalPendingBase64 || currentImage || null;

    const handleSave = async () => {
        const raw = externalPendingBase64 || coverImageFile?.compressedBase64;
        if (!raw) return;
        setIsSaving(true);
        const pureBase64 = raw.includes(",") ? raw.split(",")[1] : raw;
        const response = await webApiClient.patch("/api/v2/instructor/manage/profile/cover", {
            base64Image: pureBase64,
        });
        setIsSaving(false);
        if (response.ok) {
            toast.success("Cover image saved.");
            setCoverImageFile(null);
            setCoverImageChanged(false);
            if (onPreviewChange) onPreviewChange(null);
            if (onExternalCancel) onExternalCancel();
            if (onSaveSuccess) onSaveSuccess(raw, response.data);
        } else {
            const msg = response.data?.message || "Failed to save cover image. Please try again.";
            toast.error(msg);
            if (onSaveError) onSaveError(response);
        }
    };

    const handleCancel = () => {
        setCoverImageFile(null);
        setCoverImageChanged(false);
        if (onPreviewChange) onPreviewChange(null);
        if (onExternalCancel) onExternalCancel();
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        const response = await webApiClient.patch("/api/v2/instructor/manage/profile/cover", {
            base64Image: null,
        });
        setIsDeleting(false);
        if (response.ok) {
            toast.success("Cover image removed.");
            if (onDeleteSuccess) onDeleteSuccess(response.data);
        } else {
            toast.error(response.data?.message || "Failed to remove cover image. Please try again.");
        }
    };

    return (
        <div className="instructor-cover-upload">
            <ImageUpload
                onImageSelect={imageData => {
                    if (onExternalCancel) onExternalCancel();
                    setCoverImageFile(imageData);
                    setCoverImageChanged(true);
                    if (onPreviewChange) onPreviewChange(imageData.thumbnail || imageData.compressedBase64 || null);
                }}
                currentImage={displayImage}
                maxWidth={1200}
                compressionQuality={0.2}
                thumbnailQuality={0.3}
            />
            {hasPendingChanges ? (
                <div className="instructor-cover-upload-actions">
                    <button
                        type="button"
                        className="btn-purple btn-small"
                        onClick={handleSave}
                        disabled={isSaving}>
                        {isSaving ? "Saving..." : "Save"}
                    </button>
                    <button
                        type="button"
                        className="btn-gray btn-small"
                        onClick={handleCancel}
                        disabled={isSaving}>
                        Cancel
                    </button>
                </div>
            ) : currentImage && (
                <div className="instructor-cover-upload-actions">
                    <button
                        type="button"
                        className="btn-danger btn-small"
                        onClick={handleDelete}
                        disabled={isDeleting}>
                        {isDeleting ? "Removing..." : "Remove Cover"}
                    </button>
                </div>
            )}
        </div>
    );
};

export default InstructorCoverUpload;
